import {Component, Input, OnChanges, SimpleChanges} from "@angular/core";
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {ValidationService} from "../../../services/validation.service";
import {DYNAMIC_FIELDS} from "../dynamic-fields.config";
import {DetailService} from "../../../services/details/detail.service";
import {ActivatedRoute} from "@angular/router";
import {SharedDataService} from "../../../services/shared/shared-data.service";
import {catchError, map} from "rxjs/operators";
import {HelperService} from "../../../services/helperService";
import {ENVIRONMENT} from "../../../../environments/environment";
import {DetailConfigurationField} from "../../../models/detail-configuration-field";
import {of} from "rxjs";
import {ContentsHelper} from "../../../helpers/contents.helper";

@Component({
    exportAs: "dynamicForm",
    selector: "tig-dynamic-form",
    templateUrl: "./dynamic-form.template.html"
})
export class DynamicFormComponent implements OnChanges
{

	@Input()
	ARR_form_fields: DetailConfigurationField[] = [];
	@Input()
	OBJ_parent_group: FormGroup = null;
	@Input()
	STR_form_group_name: string;
	OBJ_form_group: FormGroup = null;
	private STR_name: string;
	private STR_id: string;
	private STR_url: string;
	private BOO_exact_match: boolean = true;
	public BOO_development = ENVIRONMENT.env === 'development';


	/**
	 * Function > constructor
	 * @param {FormBuilder} OBJ_form_builder
	 * @param {DetailService} OBJ_detail_service
	 * @param {ValidationService} OBJ_validation_Service
	 * @param OBJ_route
	 * @param OBJ_shared_service
	 * @param OBJ_helper_service
	 */
	constructor(
		private OBJ_form_builder: FormBuilder,
		private OBJ_detail_service: DetailService,
		private OBJ_validation_Service: ValidationService,
		private OBJ_route: ActivatedRoute,
		private OBJ_shared_service: SharedDataService,
		private OBJ_helper_service: HelperService
	)
	{} // end function constructor

	/**
	 * LifecycleHook > ngOnChanges
	 */
	ngOnChanges(changes: SimpleChanges): void
	{
		this.OBJ_form_group = this.createGroup();

		if (this.OBJ_parent_group)
		{
			this.OBJ_parent_group.addControl(this.STR_form_group_name, this.OBJ_form_group);
		} // end if

	} // end lifecycle hook ngOnChanges

	/**
	 * Function > createGroup
	 * @returns {FormGroup}
	 */
	private createGroup(ARR_config_fields = this.ARR_form_fields)
	{

		let OBJ_form_group = this.OBJ_form_builder.group({});
		for (let OBJ_field_config of ARR_config_fields) {

			// embedded object - do not add tabs or not defined fields
			if (
				!OBJ_field_config.tab &&
				typeof DYNAMIC_FIELDS[OBJ_field_config.type] !== "undefined" &&
				typeof OBJ_field_config.fields !== 'undefined')
			{
				// create form group for fields
				let OBJ_group = this.createGroup(OBJ_field_config.fields);
				OBJ_form_group.addControl(OBJ_field_config.name, OBJ_group);
			}
			// add each field as its own FormControl, as far as the type is known
			else if (typeof DYNAMIC_FIELDS[OBJ_field_config.type] !== "undefined")
			{
				let ARR_validators = [];
				let ARR_validators_async = [];
				// if url or email has been passed as input_type, add appropriate validators
				if (OBJ_field_config.input_type == "url")
				{
					OBJ_field_config.validators.push({"type":"pattern", "value":"(http|https):\/\/.*"});
				}
				else if (OBJ_field_config.input_type == "email")
				{
					OBJ_field_config.validators.push({"type":"pattern", "value":"\.+@\.+"});
				}
				// Ueberbrueckung: input_type:number funktioniert in Firefox nicht und bringt validation durcheinander. Deshalb durch pattern ersetzt
				// Bis das aber ueberall in den configs verbaut ist, wird hier input_type zu pattern-validator uebersetzt
				else if(OBJ_field_config.input_type && OBJ_field_config.input_type === "number")
				{
					let STR_value = this.OBJ_helper_service.getPatternByName("oNumber.sAnyNumber");
					ARR_validators.push(Validators.pattern(STR_value));
				} // end if

				if (OBJ_field_config.validators)
				{

					OBJ_field_config.validators.forEach((OBJ_validator) =>
					{

						switch (OBJ_validator.type)
						{

							case "required":
								ARR_validators.push(Validators.required);
								break;

							case "minLength":
								ARR_validators.push(Validators.minLength(OBJ_validator.value));
								break;

							case "maxLength":
								ARR_validators.push(Validators.maxLength(OBJ_validator.value));
								break;

							case "min":
								ARR_validators.push(Validators.min(OBJ_validator.value));
								break;

							case "max":
								ARR_validators.push(Validators.max(OBJ_validator.value));
								break;

							case "requiredTrue":
								ARR_validators.push(Validators.requiredTrue);
								break;

							case "pattern":
								let STR_value = OBJ_validator.value;
								if (typeof OBJ_validator.name !== "undefined")
								{
									STR_value = this.OBJ_helper_service.getPatternByName(OBJ_validator.name);
								} // end if
								ARR_validators.push(Validators.pattern(STR_value));
								break;

							case "checkEmail":
								ARR_validators.push(ValidationService.checkEmail);
								break;

							case "checkPassword":
								ARR_validators.push(ValidationService.checkPassword);
								break;

							case "checkUrl":
								ARR_validators.push(ValidationService.checkUrl);
								break;

							case "numeric":
								ARR_validators.push(ValidationService.numeric);
								break;

							case "json":
								ARR_validators.push(ValidationService.json);
								break;

							case "checkApiMatch":
								ARR_validators_async.push(this.checkApiMatchUnique.bind(this));
								ARR_validators.push(Validators.required);
								this.STR_url = OBJ_field_config._links.options.href;
								this.STR_name = OBJ_field_config.name;
								this.STR_id = this.OBJ_route.snapshot.paramMap.get('id');
								if (OBJ_validator.exact_match === false) {
									this.BOO_exact_match = false;
								}
								break;

						} // end switch

					}); // end foreach

				} // end if

				OBJ_form_group.addControl
				(
					OBJ_field_config.name,
					new FormControl({
						value: OBJ_field_config.value,
						disabled: OBJ_field_config.readonly
					}, {
						validators: Validators.compose(ARR_validators),
						asyncValidators: Validators.composeAsync(ARR_validators_async),
						// select and update on blur dont combine well, the value is not changes when it is sent to its dependent fields
						updateOn: (OBJ_field_config.type === 'select' || OBJ_field_config.type === 'checkbox' || OBJ_field_config.type === 'select_multiple_check')?'change': 'blur'
					})
				);
			} // end if

			let OBJ_form_control = OBJ_form_group.get(OBJ_field_config.name);
			if (OBJ_form_control)
			{
				OBJ_form_control.valueChanges.subscribe(
					value => {

						// only commence when a user interaction caused the change of value
						if(OBJ_form_control.dirty)
						{
							// mark field as dirty - not yet used, later on maybe to only update edited fields
							OBJ_field_config.dirty = true;

							this.OBJ_helper_service.logWithClassAndFunction("DynamicFormComponent", "createGroup", "sending value from dynamic form component", value);
							// send notification about value change - whether or not any field listens to this is up to them
							this.OBJ_shared_service.sendValue(OBJ_field_config.name, value);
						}
					}
				);
			} // end if

		} // end for

		return OBJ_form_group;

	} // end function createGroup

	/**
	* Validator for Unique Api Requests
	* @param control
	*/
	checkApiMatchUnique(control: AbstractControl)
	{

		let OBJ_params = {
		'apiUrl': this.STR_url,
		'name': this.STR_name,
		'itemId': this.STR_id,
		'control': control,
		'exact_match': this.BOO_exact_match
		};

		// check if field is touched or dirty
		if (control.touched || control.dirty) {
			// if it is, request to the api
			return this.OBJ_validation_Service.checkApiMatch(OBJ_params).pipe(
				catchError(err => {
					return of([]);
				}),
				map(
					res => {
						// Wenn Api erfolgreich geantwortet hat ist Rueckgabe vom Typ ContentsHelper
						if(res instanceof ContentsHelper)
						{
							if (res.getListingData().length > 0) {
								return {ApiNotUnique: true};
							} else {
								return null;
							} // end if
						}
						// Ansonsten hat das Backend Probleme - Feld kann NICHT validiert werden
						else
						{
							// Gib andere Validationmessage zurueck
							return {backendError: true};
						}
					}
				) // end map
			); // end pipe
		} else {
			// else return null
			return new Promise((resolve) => {
				resolve(null)
			});
		}
	} // end function checkApiMatchUnique


} // end class DynamicFormComponent
